"use client"

/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Reusable pricing block — renders the plan catalogue from the API
 *   PROPS:   ctaHref (link mode) XOR onSelect (callback mode), ctaLabel?, showHeader?, className?, authAware?
 *   STATE:   delegates data fetching to usePricing()
 *   DEPENDS: M-UI-CARD, M-UI-BUTTON, M-UI-BADGE, M-USE-PRICING, M-USE-AUTH, next-intl, next/link
 *   EMITS:   onSelect(planCode) when a plan is chosen (checkout mode)
 *   NOTE:    authAware (default false) — when true AND user is logged in, override ctaHref/label
 *            to send them to /dashboard instead of the passed ctaHref. Used by the landing page only;
 *            in-app usages (dashboard/checkout) must NOT set it so logged-in users reach checkout.
 *   ACCESSIBILITY: cards are region landmarks; CTAs are focusable buttons/links, min 44px
 *   LINKS:   M-FEATURE-PRICING-PLANS
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-07-08] CLAWE-161: Make auth-aware CTA opt-in via `authAware` prop — fixes broken plan clicks on dashboard/checkout for logged-in users
 *   [2026-07-05] CLAWE-159: Auth-aware CTA — override ctaHref/label for logged-in users (link-mode only)
 *   [2026-06-16] Initial reusable pricing component, DB-driven, used on landing + dashboard + checkout
 */

import Link from "next/link"
import { useLocale, useTranslations } from "next-intl"
import { CheckCircle, Sparkle } from "@phosphor-icons/react/dist/ssr"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { usePricing, type Plan } from "@/hooks/use-pricing"
import { useAuth } from "@/hooks/use-auth"

interface PricingPlansProps {
  ctaHref?: string
  onSelect?: (planCode: string) => void
  ctaLabel?: string
  /**
   * When true AND a user is logged in, link-mode CTAs are overridden to point
   * at /dashboard (used by the public landing page). Default false — explicit
   * ctaHref/ctaLabel from the parent is respected (e.g. dashboard → checkout).
   */
  authAwareCta?: boolean
  showHeader?: boolean
  className?: string
  authAware?: boolean
}

function formatPrice(plan: Plan, locale: string): string {
  const amount = Number(plan.price_amount)
  const formatted = new Intl.NumberFormat(locale === "ru" ? "ru-RU" : "en-US").format(amount)
  const symbol = plan.price_currency === "RUB" ? "₽" : plan.price_currency
  return `${formatted} ${symbol}`
}

export function PricingPlans({
  ctaHref,
  onSelect,
  ctaLabel,
  authAwareCta = false,
  showHeader = true,
  className = "",
  authAware = false,
}: PricingPlansProps) {
  const tpr = useTranslations("pricing_new")
  const tc = useTranslations("cta_new")
  const tNav = useTranslations("nav")
  const locale = useLocale()
  const { plans, loading, error } = usePricing()
  const { user } = useAuth()

  if (loading) {
    return (
      <div className={`grid grid-cols-1 gap-6 md:grid-cols-3 ${className}`}>
        {[0, 1, 2].map((i) => (
          <Card key={i} variant="default" className="animate-fade-in">
            <CardContent className="p-6">
              <div className="mb-2 h-6 w-1/3 rounded-sm bg-surface-elevated-dark" />
              <div className="mb-6 h-9 w-2/3 rounded-sm bg-surface-elevated-dark" />
              <div className="mb-2 h-4 w-full rounded-sm bg-surface-elevated-dark" />
              <div className="mb-6 h-4 w-3/4 rounded-sm bg-surface-elevated-dark" />
              <div className="h-10 w-full rounded-md bg-surface-elevated-dark" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error || plans.length === 0) {
    return (
      <p className="text-center text-sm text-muted">
        {error ?? tpr("empty")}
      </p>
    )
  }

  // --- Auth-aware CTA: only override for explicit opt-in (landing page) ---
  const isAuthAware = authAware && Boolean(user)
  const buttonLabel = isAuthAware ? tNav("dashboardCta") : (ctaLabel ?? tc("button"))
  const effectiveCtaHref = isAuthAware ? `/${locale}/dashboard` : ctaHref

  return (
    <div className={showHeader ? "" : className}>
      {showHeader && (
        <div className={`mb-12 text-center ${className}`}>
          <div className="mb-4">
            <Badge variant="warning">{tpr("badge")}</Badge>
          </div>
          <h2 className="text-3xl text-on-dark md:text-display-md">{tpr("title")}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted">{tpr("desc")}</p>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-6 md:grid-cols-3 ${showHeader ? "" : className}`}>
        {plans.map((plan) => (
          <Card
            key={plan.code}
            variant="default"
            className="group flex h-full flex-col transition-all hover:-translate-y-1 hover:border-primary/60"
          >
            <CardContent className="flex flex-1 flex-col items-center p-6 text-center">
              <h3 className="text-xl font-semibold text-on-dark">{plan.name}</h3>
              {plan.is_highlighted && (
                <Badge variant="success" className="mt-2">
                  <Sparkle weight="fill" className="h-3 w-3" aria-hidden="true" />
                  {tpr("pro_badge")}
                </Badge>
              )}
              <p className="mb-1 mt-3 font-number text-display-md text-primary">{formatPrice(plan, locale)}</p>
              <p className="mb-4 text-xs text-muted">{tpr("per_month")}</p>
              {plan.description && <p className="mb-4 text-sm text-muted">{plan.description}</p>}
              <ul className="mb-6 w-full space-y-2 text-left">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted">
                    <CheckCircle weight="fill" className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto w-full">
                {onSelect ? (
                  <Button
                    variant="secondary"
                    className="w-full transition-all duration-150 group-hover:bg-primary group-hover:text-on-primary"
                    onClick={() => onSelect(plan.code)}
                  >
                    {buttonLabel}
                  </Button>
                ) : effectiveCtaHref ? (
                  <Link href={effectiveCtaHref}>
                    <Button
                      variant="secondary"
                      className="w-full transition-all duration-150 group-hover:bg-primary group-hover:text-on-primary"
                    >
                      {buttonLabel}
                    </Button>
                  </Link>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
